import * as Sentry from '@sentry/react';

type ReduxApiExecutionType = 'query' | 'mutation' | 'unknown';
type ReduxApiErrorLevel = 'warning' | 'error';

type RtkQueryArgMeta = {
  type?: ReduxApiExecutionType;
  endpointName?: string;
  originalArgs?: unknown;
};

type RtkQueryBaseQueryMeta = {
  request?: Request;
  response?: Response;
};

export type RtkQueryRejectedAction = {
  type: string;
  payload?: unknown;
  meta?: {
    arg?: RtkQueryArgMeta;
    baseQueryMeta?: RtkQueryBaseQueryMeta;
    requestId?: string;
    aborted?: boolean;
    condition?: boolean;
  };
  error?: {
    name?: string;
    message?: string;
    stack?: string;
    code?: string;
  };
};

type RtkQueryErrorPayload = {
  status?: number | string;
  data?: unknown;
  error?: string;
};

type RequestLikeArgs = {
  url?: unknown;
  method?: unknown;
};

const MAX_TAG_LENGTH = 200;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const safeString = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return fallback;
};

const truncateTag = (value: string): string => {
  return value.length > MAX_TAG_LENGTH ? `${value.slice(0, MAX_TAG_LENGTH - 3)}...` : value;
};

const getReducerPath = (actionType: string): string => {
  const [reducerPath] = actionType.split('/');
  return reducerPath || 'unknownApi';
};

const getExecutionType = (action: RtkQueryRejectedAction): ReduxApiExecutionType => {
  const metaType = action.meta?.arg?.type;

  if (metaType === 'query' || metaType === 'mutation') {
    return metaType;
  }

  if (action.type.includes('/executeMutation/')) {
    return 'mutation';
  }

  if (action.type.includes('/executeQuery/')) {
    return 'query';
  }

  return 'unknown';
};

const getRequestMethodFromOriginalArgs = (originalArgs: unknown): string | undefined => {
  if (typeof originalArgs === 'string') {
    return 'GET';
  }

  if (!isRecord(originalArgs)) {
    return undefined;
  }

  const args = originalArgs as RequestLikeArgs;
  return typeof args.method === 'string' ? args.method.toUpperCase() : undefined;
};

const getRequestUrlFromOriginalArgs = (originalArgs: unknown): string | undefined => {
  if (typeof originalArgs === 'string') {
    return originalArgs;
  }

  if (!isRecord(originalArgs)) {
    return undefined;
  }

  const args = originalArgs as RequestLikeArgs;
  return typeof args.url === 'string' ? args.url : undefined;
};

const normalizeUrl = (url: string | undefined): string => {
  if (!url) {
    return 'unknown-url';
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname;
  } catch {
    const [path] = url.split('?');
    return path || 'unknown-url';
  }
};

const getStatus = (payload: unknown): string => {
  if (!isRecord(payload)) {
    return 'unknown';
  }

  return safeString(payload.status, 'unknown');
};

const getStatusNumber = (status: string): number | undefined => {
  const parsed = Number(status);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getStatusFamily = (status: string): string => {
  const statusNumber = getStatusNumber(status);

  if (!statusNumber) {
    return status.toLowerCase().replace(/[^a-z0-9_-]/gi, '_') || 'unknown';
  }

  return `${Math.floor(statusNumber / 100)}xx`;
};

const getSentryLevel = (status: string): ReduxApiErrorLevel => {
  const statusNumber = getStatusNumber(status);

  if (statusNumber && statusNumber >= 400 && statusNumber < 500) {
    return 'warning';
  }

  return 'error';
};

const getMessageFromData = (data: unknown): string | undefined => {
  if (!isRecord(data)) {
    return undefined;
  }

  const message = data.message ?? data.title ?? data.detail;
  return typeof message === 'string' && message.trim() ? message.trim() : undefined;
};

const getBackendErrorCode = (data: unknown): string => {
  if (!isRecord(data)) {
    return 'none';
  }

  return safeString(data.code ?? data.errorCode ?? data.error_code, 'none');
};

const getErrorData = (payload: unknown): unknown => {
  if (!isRecord(payload)) {
    return undefined;
  }

  return payload.data;
};

const getTransportErrorMessage = (payload: unknown): string | undefined => {
  if (!isRecord(payload)) {
    return undefined;
  }

  return typeof payload.error === 'string' && payload.error.trim()
    ? payload.error.trim()
    : undefined;
};

const getIssueMessage = (params: {
  reducerPath: string;
  endpointName: string;
  executionType: ReduxApiExecutionType;
  status: string;
  data: unknown;
  payload: unknown;
}): string => {
  const backendMessage = getMessageFromData(params.data);
  const transportMessage = getTransportErrorMessage(params.payload);
  const detail = backendMessage ?? transportMessage;

  const prefix = `Redux API ${params.executionType} failed: ${params.reducerPath}.${params.endpointName} [${params.status}]`;
  return detail ? `${prefix} - ${detail}` : prefix;
};

export const isRtkQueryErrorPayload = (payload: unknown): payload is RtkQueryErrorPayload => {
  if (!isRecord(payload)) {
    return false;
  }

  return 'status' in payload && ('data' in payload || 'error' in payload);
};

export const isRtkQueryPlainObjectRejection = (value: unknown): boolean => {
  return isRtkQueryErrorPayload(value);
};

export const captureReduxApiError = (action: RtkQueryRejectedAction): void => {
  const payload = action.payload;

  if (!isRtkQueryErrorPayload(payload)) {
    return;
  }

  const reducerPath = getReducerPath(action.type);
  const endpointName = safeString(action.meta?.arg?.endpointName, 'unknownEndpoint');
  const executionType = getExecutionType(action);
  const status = getStatus(payload);
  const statusFamily = getStatusFamily(status);
  const level = getSentryLevel(status);
  const data = getErrorData(payload);
  const requestUrl = normalizeUrl(
    action.meta?.baseQueryMeta?.request?.url ??
      getRequestUrlFromOriginalArgs(action.meta?.arg?.originalArgs)
  );
  const requestMethod =
    action.meta?.baseQueryMeta?.request?.method ??
    getRequestMethodFromOriginalArgs(action.meta?.arg?.originalArgs) ??
    'GET';
  const backendErrorCode = getBackendErrorCode(data);
  const message = getIssueMessage({
    reducerPath,
    endpointName,
    executionType,
    status,
    data,
    payload,
  });
  const error = new Error(message);
  error.name = 'ReduxApiRequestError';

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag('error.source', 'redux-api');
    scope.setTag('redux.api', truncateTag(reducerPath));
    scope.setTag('redux.endpoint', truncateTag(endpointName));
    scope.setTag('redux.execution_type', executionType);
    scope.setTag('http.status_code', status);
    scope.setTag('http.status_family', statusFamily);
    scope.setTag('http.method', requestMethod);
    scope.setTag('http.route', truncateTag(requestUrl));
    scope.setTag('backend.error_code', truncateTag(backendErrorCode));

    if (action.meta?.requestId) {
      scope.setTag('redux.request_id', truncateTag(action.meta.requestId));
    }

    scope.setFingerprint([
      'redux-api',
      reducerPath,
      endpointName,
      executionType,
      status,
      backendErrorCode,
    ]);

    scope.setContext('redux_api', {
      reducerPath,
      endpointName,
      executionType,
      actionType: action.type,
      requestId: action.meta?.requestId ?? 'not-set',
      aborted: action.meta?.aborted ?? false,
      condition: action.meta?.condition ?? false,
    });

    scope.setContext('http', {
      method: requestMethod,
      route: requestUrl,
      status,
      statusFamily,
    });

    scope.setContext('backend_response', {
      data: data ?? null,
      transportError: getTransportErrorMessage(payload) ?? null,
    });

    Sentry.captureException(error);
  });
};
